<template>
  <LineChart
    :request-status="requestStatus"
    :series="series"
    :title="title"
    :item-id="itemId"
    :card-options="cardOptions"
    :show-granularity="false"
    v-bind="$attrs"
    @reload="getData"
  />
</template>

<script>
import LineChart from '@/components/cards/models/LineChart';
import channelModel from '@/mixins/channel-model';
import cardsModel from '@/mixins/cards-model';
import { mapActions, mapGetters } from 'vuex';
import { ENTITIES, OPERATION, USERS_FIELDS } from '@/services/charts-service';
import { formatTitleAndChannel } from '@/utils/channels';

const filterByMetricDate = (item) => item.label === 'metricDate';
const createPage = (point) => ({
  label: formatTitleAndChannel(point.coordinates),
  channel: point.coordinates.channel,
  metricDate: point.coordinates.metricDate,
  value: point.value,
});
const parseGroupByChannelAndUsername = (points) => points?.reduce((prev, curr) => {
  if (!prev[curr.label]) prev[curr.label] = { name: curr.label, channel: curr.channel, data: {} };
  prev[curr.label].data[curr.metricDate] = curr.value;
  return prev;
}, {});
const getFansDifference = (currentValue, previousValue) => currentValue - previousValue;
const prepareDataValues = (data) => {
  const values = [];
  for (let index = 2; index < data.length; index++) {
    const previousValue = data[index - 1].value;
    const currentValue = data[index].value;
    const newFans = previousValue && getFansDifference(currentValue, previousValue);
    values.push({
      x: data[index - 1].date,
      y: newFans || 0,
    });
  }
  return values;
};

export default {
  name: 'FanBaseEvolutionCompetitiveChart',
  components: {
    LineChart,
  },
  mixins: [
    channelModel,
    cardsModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      requestStatus: 'loading',
      rawSeries: [],
      data: [],
      classifiedPercentage: 0,
      cardOptions: {
        chart: {
          type: 'spline',
        },
        xAxis: {},
        yAxis: {
          min: null,
          plotLines: [{
            color: '#515156',
            width: 1,
            value: 0,
          }],
          labels: {
            enabled: true,
          },
        },
        legend: {
          enabled: true,
        },
        plotOptions: {
          series: {
            marker: {
              enabled: false,
            },
          },
        },
      },
    };
  },
  computed: {
    ...mapGetters('dashboard', [
      'activeDashboard',
    ]),
    series() {
      const points = this.data?.points?.map(point => createPage(point));
      const groupByChannelAndUsername = parseGroupByChannelAndUsername(points);
      const series = groupByChannelAndUsername && Object.values(groupByChannelAndUsername);

      const parsedSeries = series?.map(({ name, data }) => {
        const normalizedCategoriesData = this.categories?.map(timestamp => {
          return ({
            date: timestamp,
            value: data[timestamp] || null,
          });
        });
        const preparedDataValues = prepareDataValues(normalizedCategoriesData);

        return {
          name,
          data: preparedDataValues,
        };
      });
      this.categories?.shift();
      return parsedSeries;
    },
    categories() {
      const { axes } = this.data;
      const metricDateList = axes?.filter(filterByMetricDate)[0];
      return metricDateList?.values;
    },
    pages() {
      return this.activeDashboard.data.pages;
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getChartsService',
    ]),
    async getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }

      await this.getChartsService({
        itemId: this.itemId,
        method: 'getAggregatedData',
        increaseEndDay: true,
        entity: ENTITIES.USERS,
        points_parser: '',
        params: {
          fields: [USERS_FIELDS.FOLLOWERS],
          grouping: [USERS_FIELDS.CHANNEL, USERS_FIELDS.USERNAME, USERS_FIELDS.DATE, USERS_FIELDS.UID],
          operators: OPERATION.LAST,
          datePortion: this.getDatePortion(options),
        },
      })
        .then(response => {
          this.data = response;

          this.requestStatus = 'success';
        })
        .catch(error => {
          console.error(error);
          this.requestStatus = 'error';
        });
    },
  },
};
</script>
